import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg';
import { toast } from 'react-toastify';
import { apiService } from 'services/base.service';
import TransferStatusTag from 'components/base/transfer-status-tag';
import { ENUM_TRANSFER_STATUS, TransferRequestType } from 'models/transfer-request.model';
import RequestDetailsWrapper from 'components/base/request-details-wrapper';
import useDashboardContext from 'contexts/dashboard.context';
import {
  buildTransferRoute,
  getTransferStoreTypeFromPathname,
  normalizeTransferStoreType
} from '../store-type';
import { DefaultTransfer } from 'utils/constants';

const UpdateTransferRequest = () => {
  const [transfer, setTransfer] = useState<TransferRequestType>(DefaultTransfer);
  const [isFetchingTransfer, setIsFetchingTransfer] = useState(false);
  const { activeStore } = useDashboardContext();
  const router = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const storeType = normalizeTransferStoreType(getTransferStoreTypeFromPathname(location.pathname));

  const getTransferDetails = async () => {
    if (!id) return;
    try {
      setIsFetchingTransfer(true);
      const response: any = await apiService.get(`main-store-transfer/${storeType}/${id}`, {
        version: 'v2'
      });
      if (response?.data?.data) {
        setTransfer(response.data.data);
      }
    } catch (err: any) {
      toast(
        <ToastMessage
          message={err.response?.data?.message || 'Error fetching transfer request details'}
          type='error'
        />
      );
    } finally {
      setIsFetchingTransfer(false);
    }
  };

  useEffect(() => {
    getTransferDetails();
  }, [id, storeType]);

  const handleEdit = () => {
    if (!transfer.id) return;
    router(buildTransferRoute(storeType, `update-details/${transfer.id}`));
  };

  const isCompleted = transfer.status === ENUM_TRANSFER_STATUS.RECEIVED;
  const isRequestedByActiveStore =
    (transfer.status === ENUM_TRANSFER_STATUS.REQUESTED &&
      activeStore?.id !== transfer.from_store_id) ||
    (transfer.status === ENUM_TRANSFER_STATUS.SENT && activeStore?.id === transfer.from_store_id);
  const canEditTransfer = !isCompleted && !isRequestedByActiveStore;

  return (
    <RequestDetailsWrapper
      codeId={transfer.id?.substring(0, 5) || ''}
      statusTag={<TransferStatusTag type={transfer.status} />}
      header={isCompleted ? 'Request Completed' : 'Update request'}
      subHeader={
        !isCompleted ? 'Update item as received or returned once vendor delivers' : undefined
      }>
      {isFetchingTransfer ? (
        <div className='flex justify-center items-center h-[300px]'>
          <p>Loading transfer details...</p>
        </div>
      ) : (
        <>
          {transfer.items.map((item, index) => (
            <div
              className='flex justify-between py-[24px] px-[28px] border-b border-b-[#F7F7F8]'
              key={index}>
              <div className='flex gap-4 items-center'>
                <p className='font-medium text-[#2E3031] capitalize'>{item?.ingredient_name}</p>
                <div className='space-y-5 ml-3'>
                  <p className='text-sm text-[#2E3031] capitalize font-semibold'>
                    <span className='font-medium'>Quantity: </span> {item?.quantity} requested
                  </p>
                  <p className='text-sm text-[#2E3031] capitalize font-semibold'>
                    <span className='font-medium'>Quantity sent: </span> {item?.quantity_sent ?? 0}{' '}
                    sent
                  </p>
                  <p className='text-sm text-[#2E3031] capitalize font-semibold'>
                    <span className='font-medium'>Quantity received: </span>{' '}
                    {item?.quantity_received ?? 0} received
                  </p>
                </div>
              </div>
              <div className='flex items-center gap-6'>
                {canEditTransfer && <EditIcon onClick={handleEdit} className='cursor-pointer' />}
                <TransferStatusTag type={transfer.status} />
              </div>
            </div>
          ))}
          <div>
            <hr className='border-t-4 border-[#F7F7F8]' />

            <div className='flex gap-6 items-center justify-end mt-6 mx-[28px] ml-auto'>
              <Button
                variant='block'
                color='primary'
                small
                className='w-[153px] h-[43px]'
                onClick={() => router(-1)}>
                Back
              </Button>
            </div>
          </div>
        </>
      )}
    </RequestDetailsWrapper>
  );
};

export default UpdateTransferRequest;
